import { useState } from 'react';
import type { FinanceLine } from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Row,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

const money = (cents: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);

const collectionLabel: Record<NonNullable<FinanceLine['collectionStatus']>, string> = {
  ready: 'Ready to charge',
  processing: 'Processing',
  succeeded: 'Collected',
  failed: 'Failed',
  needs_review: 'Needs review',
};

export function Payments() {
  const { data, loading, error, reload } = useAsync(() => adminApi.finance.overview());
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const run = async (earningId: string, action: 'charge' | 'reconcile') => {
    setBusy(earningId);
    setActionError(null);
    try {
      if (action === 'charge') await adminApi.finance.charge(earningId);
      else await adminApi.finance.reconcile(earningId);
      reload();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : 'Couldn’t update collection');
      reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeader title="Payments" />
      {loading ? (
        <SkeletonList count={3} />
      ) : error || !data ? (
        <EmptyState title="Couldn’t load payments" {...(error ? { description: error } : {})} />
      ) : (
        <Stack gap={20}>
          <Card>
            <Row gap={8} style={{ alignItems: 'center' }}>
              <strong>Connections</strong>
              <Badge tone={data.provider.stripeConfigured ? 'success' : 'warning'}>
                Stripe {data.provider.stripeConfigured ? 'key connected' : 'needs key'}
              </Badge>
              <Badge tone={data.provider.workMarketConfigured ? 'success' : 'warning'}>
                Contractor payouts {data.provider.workMarketConfigured ? 'keys set' : 'need setup'}
              </Badge>
            </Row>
          </Card>
          <FeeSetting />
          {actionError ? (
            <span className="dsp-field__error" role="alert">
              {actionError}
            </span>
          ) : null}
          <section aria-labelledby="collections-heading">
            <h2 id="collections-heading" style={{ fontSize: '1.1rem' }}>
              Collections
            </h2>
            {data.collections.length === 0 ? (
              <EmptyState title="No approved hours yet" />
            ) : (
              <Stack gap={10}>
                {data.collections.map((line) => (
                  <Card key={line.earningId}>
                    <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <strong>{line.groupName}</strong>
                        <div className="dsp-muted" style={{ marginTop: 4 }}>
                          {line.professionalName} · approved{' '}
                          {new Date(line.approvedAt).toLocaleDateString()}
                        </div>
                      </div>
                      <Badge
                        tone={
                          line.collectionStatus === 'succeeded'
                            ? 'success'
                            : line.collectionStatus === 'failed' ||
                                line.collectionStatus === 'needs_review'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {line.collectionStatus
                          ? collectionLabel[line.collectionStatus]
                          : 'Awaiting charge'}
                      </Badge>
                    </Row>
                    <p style={{ marginBottom: 0 }}>
                      <strong>{money(line.officeTotalCents)}</strong>{' '}
                      <span className="dsp-muted">
                        ({money(line.professionalGrossCents)} professional +{' '}
                        {money(line.officeFeeCents)} fee)
                      </span>
                    </p>
                    {(line.collectionStatus === 'ready' ||
                      line.collectionStatus === 'failed' ||
                      line.collectionStatus === null) &&
                    data.provider.stripeConfigured &&
                    line.practiceCardReady ? (
                      <Button
                        size="sm"
                        style={{ marginTop: 12 }}
                        disabled={busy === line.earningId}
                        onClick={() => void run(line.earningId, 'charge')}
                      >
                        {busy === line.earningId
                          ? 'Charging…'
                          : line.collectionStatus === 'failed'
                            ? 'Retry charge'
                            : 'Charge practice'}
                      </Button>
                    ) : (line.collectionStatus === 'ready' ||
                        line.collectionStatus === 'failed' ||
                        line.collectionStatus === null) &&
                      !line.practiceCardReady ? (
                      <p className="dsp-muted" style={{ marginBottom: 0 }}>
                        Practice needs a payment method.
                      </p>
                    ) : line.collectionStatus === 'processing' ||
                      line.collectionStatus === 'needs_review' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        style={{ marginTop: 12 }}
                        disabled={busy === line.earningId}
                        onClick={() => void run(line.earningId, 'reconcile')}
                      >
                        {busy === line.earningId ? 'Checking…' : 'Check payment'}
                      </Button>
                    ) : null}
                  </Card>
                ))}
              </Stack>
            )}
          </section>
          <section aria-labelledby="payouts-heading">
            <h2 id="payouts-heading" style={{ fontSize: '1.1rem' }}>
              Contractor payouts
            </h2>
            {data.payouts.length === 0 ? (
              <EmptyState title="No payouts yet" />
            ) : (
              <Stack gap={10}>
                {data.payouts.map((line) => (
                  <Card key={line.earningId}>
                    <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <strong>{line.professionalName}</strong>
                        <div className="dsp-muted" style={{ marginTop: 4 }}>
                          {line.groupName} · {money(line.professionalGrossCents)}
                        </div>
                      </div>
                      <Badge tone={line.payoutStatus === 'paid' ? 'success' : 'warning'}>
                        {humanize(line.payoutStatus)}
                      </Badge>
                    </Row>
                  </Card>
                ))}
              </Stack>
            )}
          </section>
        </Stack>
      )}
    </div>
  );
}

function FeeSetting() {
  const settings = useAsync(() => adminApi.finance.settings());
  const [fee, setFee] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const current = fee ?? (settings.data ? String(settings.data.officeFeeBasisPoints / 100) : '');

  const save = async () => {
    const officeFeeBasisPoints = Math.round(Number(current) * 100);
    if (
      !Number.isInteger(officeFeeBasisPoints) ||
      officeFeeBasisPoints < 0 ||
      officeFeeBasisPoints > 10000
    ) {
      setMessage('Enter a fee from 0% to 100%.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await adminApi.finance.updateSettings({ officeFeeBasisPoints });
      setFee(null);
      settings.reload();
      setMessage('Saved. New bookings will use this fee.');
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Couldn’t save fee');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Row style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <strong>Practice fee</strong>
            <div className="dsp-muted" style={{ marginTop: 4 }}>
              Applies to new bookings. Existing bookings keep their fee.
            </div>
          </div>
          <Row gap={8} style={{ alignItems: 'flex-end' }}>
            <Field label="Fee (%)">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                required
                value={current}
                onChange={(event) => {
                  setFee(event.target.value);
                  setMessage(null);
                }}
                disabled={settings.loading || Boolean(settings.error) || busy}
              />
            </Field>
            <Button
              type="submit"
              size="sm"
              disabled={settings.loading || Boolean(settings.error) || busy}
            >
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </Row>
        </Row>
        {settings.error ? <span className="dsp-field__error">{settings.error}</span> : null}
        {message ? (
          <span role="status" className="dsp-muted">
            {message}
          </span>
        ) : null}
      </form>
    </Card>
  );
}
